import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTelegram } from '../context/TelegramContext'
import Header from '../components/Header'
import './AdminCategories.css'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000/api'

const AdminCategories = () => {
  const navigate = useNavigate()
  const { isAdmin, showAlert, showConfirm } = useTelegram()
  const [categories, setCategories] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [isEditing, setIsEditing] = useState(false)
  const [editingCategory, setEditingCategory] = useState(null)
  const [formData, setFormData] = useState({
    name: '',
    emoji: '📦',
    display_order: 0
  })

  useEffect(() => {
    if (isAdmin !== true) {
      showAlert?.('Доступ запрещён')
      navigate('/')
      return
    }
    loadCategories()
  }, [isAdmin])

  const loadCategories = async () => {
    try {
      setLoading(true)
      const response = await fetch(`${API_URL}/categories`)
      const data = await response.json()
      
      if (data.success && Array.isArray(data.categories)) {
        setCategories(data.categories)
        setError(null)
      } else {
        setError('Не удалось загрузить категории')
      }
    } catch (err) {
      console.error('Ошибка загрузки категорий:', err)
      setError('Не удалось загрузить категории')
    } finally {
      setLoading(false)
    }
  }

  const handleAdd = () => {
    setIsEditing(true)
    setEditingCategory(null)
    setFormData({
      name: '',
      emoji: '📦',
      display_order: categories.length
    })
  }

  const handleEdit = (category) => {
    setIsEditing(true)
    setEditingCategory(category)
    setFormData({
      name: category.name,
      emoji: category.emoji,
      display_order: category.display_order
    })
  }

  const handleCancel = () => {
    setIsEditing(false)
    setEditingCategory(null)
    setFormData({
      name: '',
      emoji: '📦',
      display_order: 0
    })
  }

  const handleSave = async () => {
    if (!formData.name.trim()) {
      showAlert?.('Введите название категории')
      return
    }

    try {
      if (editingCategory) {
        // Обновление
        const response = await fetch(`${API_URL}/categories/${editingCategory.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(formData)
        })
        
        const data = await response.json()
        
        if (response.ok && data.success) {
          showAlert?.('Категория обновлена')
          setIsEditing(false)
          loadCategories()
        } else {
          showAlert?.(`Ошибка: ${data.detail || 'Не удалось обновить категорию'}`)
        }
      } else {
        // Создание
        const response = await fetch(`${API_URL}/categories`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(formData)
        })
        
        const data = await response.json()
        
        if (response.ok && data.success) {
          showAlert?.('Категория создана')
          setIsEditing(false)
          loadCategories()
        } else {
          showAlert?.(`Ошибка: ${data.detail || 'Не удалось создать категорию'}`)
        }
      }
    } catch (err) {
      console.error('Ошибка сохранения категории:', err)
      showAlert?.('Ошибка сохранения категории')
    }
  }

  const handleDelete = async (categoryId) => {
    const confirmed = await showConfirm?.('Удалить эту категорию? (Если у неё есть товары, удаление будет невозможно)')
    if (!confirmed) return

    try {
      const response = await fetch(`${API_URL}/categories/${categoryId}`, {
        method: 'DELETE'
      })
      
      const data = await response.json()
      
      if (response.ok && data.success) {
        showAlert?.('Категория удалена')
        loadCategories()
      } else {
        showAlert?.(`Ошибка: ${data.detail || 'Не удалось удалить категорию'}`)
      }
    } catch (err) {
      console.error('Ошибка удаления:', err)
      showAlert?.('Ошибка удаления категории')
    }
  }

  if (loading) {
    return (
      <div className="admin-categories">
        <Header title="Управление категориями" showBack={true} />
        <div className="loading">Загрузка...</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="admin-categories">
        <Header title="Управление категориями" showBack={true} />
        <div className="error">{error}</div>
      </div>
    )
  }

  if (isEditing) {
    return (
      <div className="admin-categories">
        <Header title={editingCategory ? 'Редактировать категорию' : 'Добавить категорию'} showBack={true} />
        
        <div className="admin-categories-container">
          <div className="form-group">
            <label>Название категории</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Например: Куртки"
              className="form-input"
            />
          </div>

          <div className="form-group">
            <label>Эмодзи</label>
            <input
              type="text"
              value={formData.emoji}
              onChange={(e) => setFormData({ ...formData, emoji: e.target.value })}
              placeholder="📦"
              className="form-input emoji-input"
              maxLength="2"
            />
            <small>Один символ эмодзи для визуального отображения</small>
          </div>

          <div className="form-group">
            <label>Порядок отображения</label>
            <input
              type="number"
              value={formData.display_order}
              onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) || 0 })}
              className="form-input"
              min="0"
            />
            <small>Чем меньше число, тем выше в списке</small>
          </div>

          <div className="form-actions">
            <button className="btn-save" onClick={handleSave}>
              💾 Сохранить
            </button>
            <button className="btn-cancel" onClick={handleCancel}>
              ❌ Отмена
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="admin-categories">
      <Header title="Управление категориями" showBack={true} />
      
      <div className="admin-categories-container">
        <button className="btn-add-category" onClick={handleAdd}>
          ➕ Добавить категорию
        </button>

        <div className="categories-list">
          {categories.length === 0 ? (
            <div className="empty-state">
              <p>Категорий пока нет</p>
              <p className="empty-hint">Нажмите кнопку выше, чтобы добавить первую категорию</p>
            </div>
          ) : (
            categories.map(category => (
              <div key={category.id} className="admin-category-item">
                <div className="admin-category-icon">{category.emoji}</div>
                
                <div className="admin-category-info">
                  <h3>{category.name}</h3>
                  <p className="category-order">Порядок: {category.display_order}</p>
                </div>

                <div className="admin-category-actions">
                  <button 
                    className="btn-edit"
                    onClick={() => handleEdit(category)}
                  >
                    ✏️
                  </button>
                  <button 
                    className="btn-delete"
                    onClick={() => handleDelete(category.id)}
                  >
                    🗑️
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  )
}

export default AdminCategories


